/**
 * Квокка-тренер: живёт в углу учебных экранов и реагирует на каждый
 * ответ — прыжок и похвала за верный, сочувствие за промах, клич на
 * комбо. Без ответов изредка машет, чтобы экран не казался пустым.
 * При «меньше движения» остаются только реплики.
 */

import { AnimatePresence, motion, useReducedMotion, type TargetAndTransition } from 'framer-motion';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { isPracticePath } from '../app/navigation';
import { useComboStore, type AnswerEvent } from '../store/comboStore';
import { Quokka, type QuokkaMood } from './Quokka';
import i18n from '../i18n';

const PRAISE = ['0', '1', '2', '3', '4'] as const;
const COMFORT = ['0', '1', '2'] as const;
const IDLE_WAVE_MS = 14000;
const REACTION_MS = 1700;
const COMBO_FROM = 3;

type Reaction = { readonly mood: QuokkaMood; readonly line: string; readonly combo: boolean };

function reactionFor(event: AnswerEvent): Reaction {
  if (!event.correct) return { mood: 'error', line: i18n.t(`coach.comfort.${COMFORT[event.id % COMFORT.length] ?? '0'}`), combo: false };
  if (event.streak >= COMBO_FROM) return { mood: 'celebrate', line: i18n.t('coach.combo', { count: event.streak }), combo: true };
  return { mood: 'celebrate', line: i18n.t(`coach.praise.${PRAISE[event.id % PRAISE.length] ?? '0'}`), combo: false };
}

const MOTION: Record<'correct' | 'combo' | 'wrong' | 'wave', TargetAndTransition> = {
  correct: { y: [0, -26, 0, -8, 0], scaleY: [1, 1.08, 0.9, 1.03, 1], transition: { duration: 0.7 } },
  combo: { y: [0, 6, -48, 0, -16, 0], scaleY: [1, 0.85, 1.12, 0.88, 1.04, 1], scaleX: [1, 1.12, 0.92, 1.1, 0.98, 1], transition: { duration: 1 } },
  wrong: { x: [0, -10, 0], scaleY: [1, 0.92, 1], transition: { duration: 0.5 } },
  wave: { y: [0, -6, 0], transition: { duration: 0.8 } },
};

export function QuokkaCoach() {
  const { pathname } = useLocation();
  const last = useComboStore((state) => state.last);
  const reset = useComboStore((state) => state.reset);
  const reduceMotion = useReducedMotion();
  const [reaction, setReaction] = useState<Reaction | null>(null);
  const [wave, setWave] = useState(0);
  const hidden = useComboStore((state) => state.coachHidden);
  const active = isPracticePath(pathname) && !hidden;

  useEffect(() => {
    reset();
  }, [pathname, reset]);

  useEffect(() => {
    if (last === null) return undefined;
    setReaction(reactionFor(last));
    const timer = window.setTimeout(() => setReaction(null), REACTION_MS);
    return () => window.clearTimeout(timer);
  }, [last]);

  useEffect(() => {
    if (!active) return undefined;
    const timer = window.setInterval(() => setWave((count) => count + 1), IDLE_WAVE_MS);
    return () => window.clearInterval(timer);
  }, [active]);

  if (!active) return null;

  const pose = reaction === null ? 'wave' : reaction.combo ? 'combo' : reaction.mood === 'error' ? 'wrong' : 'correct';
  const animateKey = reaction === null ? `wave-${wave}` : `answer-${last?.id ?? 0}`;

  return (
    <div className="pointer-events-none fixed inset-x-0 top-14 z-40 flex justify-center sm:inset-x-auto sm:bottom-[calc(var(--lesson-footer)+1.5rem)] sm:right-6 sm:top-auto sm:items-end sm:gap-2">
      <AnimatePresence>
        {reaction !== null && (
          <motion.p
            key={last?.id}
            role="status"
            initial={{ opacity: 0, y: 10, scale: 0.8 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.9 }}
            transition={{ type: 'spring', stiffness: 420, damping: 24 }}
            className={[
              'max-w-[16rem] rounded-card sm:mb-14 sm:max-w-[12rem] rounded-br-md px-4 py-2 font-heading text-sm font-bold shadow-soft',
              reaction.combo ? 'bg-accent text-on-accent' : reaction.mood === 'error' ? 'glass text-heading' : 'bg-correct text-on-correct',
            ].join(' ')}
          >
            {reaction.line}
          </motion.p>
        )}
      </AnimatePresence>
      <motion.div
        className="hidden sm:block"
        initial={{ y: 120, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ type: 'spring', stiffness: 220, damping: 18, delay: 0.3 }}
      >
        <motion.div key={animateKey} style={{ transformOrigin: '50% 100%' }} animate={reduceMotion ? undefined : MOTION[pose]}>
          <Quokka mood={reaction?.mood ?? 'idle'} alt="" size="lg" />
        </motion.div>
      </motion.div>
    </div>
  );
}
